import React, { useState, useEffect } from 'react';
import StatusIndicator from '../ui/StatusIndicator';
import { getSystemStatus, login, requestPasswordReset } from '../../api/moduleApi';

// --- Login Screen Component ---
const LoginScreen = ({ handleLogin, backendStatus, setBackendStatus }) => {
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState(null);
  const [resetError, setResetError] = useState(null);

  useEffect(() => {
    getSystemStatus().then(data => setBackendStatus(data));
  }, [setBackendStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError(null);
    setLoadingLogin(true);
    try {
      const user = await login(email, password);
      handleLogin(user);
    } catch (err) {
      setLoginError(err.message || 'Login failed');
    } finally {
      setLoadingLogin(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError(null);
    setResetMessage(null);
    setResetLoading(true);
    try {
      const res = await requestPasswordReset(resetEmail);
      setResetMessage(res.message || 'If that email exists, a reset link has been sent.');
    } catch (err) {
      setResetError(err.message || 'Failed to request password reset.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-start pt-20 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-xl">
        <div className="mb-8 border-b-4 border-indigo-200 pb-3 flex justify-center">
          <picture>
            <source media="(max-width: 640px)" srcSet="/logo-small.svg" />
            <img
              src="/logo-text.svg"
              alt="SentinelEDU Logo"
              className="h-26 sm:h-26 w-auto"
            />
          </picture>
        </div>
        <h2 className="text-3xl font-extrabold text-indigo-800 mb-6 border-b-2 border-indigo-100 pb-3 text-center">Staff Login</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="text-sm font-medium text-gray-700">Email</label>
            <input id="login-email" value={email} onChange={e => setEmail(e.target.value)} type="email" autoComplete="email" required className="w-full mt-1 p-2 border rounded-lg" />
          </div>
          <div>
            <label htmlFor="login-password" className="text-sm font-medium text-gray-700">Password</label>
            <input id="login-password" value={password} onChange={e => setPassword(e.target.value)} type="password" autoComplete="current-password" required className="w-full mt-1 p-2 border rounded-lg" />
          </div>
          {loginError && <div role="alert" aria-live="assertive" className="text-sm text-red-600">{loginError}</div>}
          <div className="flex items-center justify-between">
            <button disabled={loadingLogin} type="submit" aria-busy={loadingLogin} className="py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg">{loadingLogin ? 'Signing in...' : 'Sign in'}</button>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(prev => !prev);
                setResetError(null);
                setResetMessage(null);
                setResetEmail(email || '');
              }}
              className="text-sm text-indigo-600 hover:underline"
            >
              Forgot password?
            </button>
          </div>
        </form>

        {showForgotPassword && (
          <form onSubmit={handleForgotPassword} className="mt-4 p-4 border border-indigo-100 bg-indigo-50 rounded-lg space-y-3">
            <h3 className="text-sm font-semibold text-indigo-800">Request password reset</h3>
            <div>
              <label htmlFor="reset-email" className="text-sm font-medium text-gray-700">Email</label>
              <input
                id="reset-email"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                type="email"
                autoComplete="email"
                required
                className="w-full mt-1 p-2 border rounded-lg"
              />
            </div>
            {resetError && <div role="alert" aria-live="assertive" className="text-sm text-red-600">{resetError}</div>}
            {resetMessage && <div className="text-sm text-green-700">{resetMessage}</div>}
            <div>
              <button disabled={resetLoading} type="submit" className="py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg">
                {resetLoading ? 'Sending...' : 'Send reset link'}
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200 w-full">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">System Status Check</h3>
          {backendStatus ? (
            <div className="space-y-3">
              <StatusIndicator label="Backend (Python/Flask)" status={backendStatus.backend_status} />
              <StatusIndicator label="Database (MySQL)" status={backendStatus.database_status} />
              <StatusIndicator 
                label={`AI Service (${(backendStatus.ai_provider || 'gemini').charAt(0).toUpperCase() + (backendStatus.ai_provider || 'gemini').slice(1)})`} 
                status={backendStatus.gemini_key_status} 
              />
              <div className="mt-4 text-sm bg-gray-50 p-3 rounded-lg text-gray-700 border-l-4 border-gray-300">
                <p className="font-semibold">Message:</p>
                <p>{backendStatus.message || backendStatus.error}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-indigo-500 font-medium">Loading system status...</div>
          )}
        </div>

        <p className="mt-8 text-xs text-gray-400 text-center">Frontend (React) on port 3000. Backend (Python) on port 5000.</p>
      </div>
    </div>
  );
};

export default LoginScreen;